'use client';

import { useMemo, type HTMLAttributes } from 'react';
import { sanitizeRichTextHtml } from 'content/science/sanitizeRichText';
import styles from './RichText.module.css';

/* ============================================================
   Rich-text design system for study detail pages.

   One typographic template, two color skins. Lifted from live
   science.ultrahuman.com .rich-text-class CSS, with the dark
   variant inverted so snapshots use the same legible scale
   as studies — only colors change. The scale (and its two
   color skins) now lives in RichText.module.css; the light
   tokens are the base classes and the dark skin overrides only
   the color custom properties via [data-variant="dark"].

   The full guide (sizes, weights, colors, when to use which
   variant) lives in docs/science-study-typography.md.
   ============================================================ */

interface Props {
  html: string;
  variant?: 'light' | 'dark';
  className?: string;
}

const RichText = ({ html, variant = 'light', className }: Props) => {
  /* Sanitize at render time so existing scraped bodies clean up
     immediately. The same function runs at CSV import time so
     freshly imported papers store clean HTML and skip this work. */
  const cleaned = useMemo(() => sanitizeRichTextHtml(html), [html]);
  return (
    <div
      className={className ? `${styles.richText} ${className}` : styles.richText}
      data-variant={variant === 'dark' ? 'dark' : undefined}
      dangerouslySetInnerHTML={{ __html: cleaned }}
    />
  );
};

export default RichText;

type BlockProps = HTMLAttributes<HTMLDivElement> & {
  $variant?: 'light' | 'dark';
};

/* ============================================================
   Author byline — used by both variants directly under the
   page title. Live spec: 24/32 italic Graphik Trial -1px.
   ============================================================ */
export const AuthorsBlock = ({ $variant, className, ...rest }: BlockProps) => (
  <div
    {...rest}
    className={
      className ? `${styles.authorsBlock} ${className}` : styles.authorsBlock
    }
    data-variant={$variant === 'dark' ? 'dark' : undefined}
  />
);

/* ============================================================
   Affiliation row — institution names, below authors.
   Live spec: 18px Graphik Trial -1px, no italic.
   ============================================================ */
export const AffiliationsBlock = ({
  $variant,
  className,
  ...rest
}: BlockProps) => (
  <div
    {...rest}
    className={
      className
        ? `${styles.affiliationsBlock} ${className}`
        : styles.affiliationsBlock
    }
    data-variant={$variant === 'dark' ? 'dark' : undefined}
  />
);

/* ============================================================
   Numbered references — last block on the page. Live spec:
   .reference-richtext p { 13/20 Graphik -0.3px }.
   Dark variant uses the same scale, recoloured.
   ============================================================ */
export const ReferenceList = ({
  $variant,
  className,
  ...rest
}: BlockProps) => (
  <div
    {...rest}
    className={
      className ? `${styles.referenceList} ${className}` : styles.referenceList
    }
    data-variant={$variant === 'dark' ? 'dark' : undefined}
  />
);
